import { Dropdown } from '@trustoo/components/Dropdown/v2'
import React, { useState } from 'react'
import { CodeSnippet, InlineCode, MIcon, Section } from './shared'

// ---------- Dropdown (v2) showcase ----------
// Generated from Figma's "Dropdown" set (5:8927). The design-system action /
// command menu on @radix-ui/react-dropdown-menu. The trigger is the single child
// (Radix asChild); the menu is data-driven via `items` sections. Checkbox rows
// need caller-owned checked state, so this showcase is stateful.

const DROPDOWN_USAGE = `import { Dropdown } from '@trustoo/components/Dropdown/v2'

// Action menu — items is an array of sections (dividers are drawn between them)
<Dropdown
    items={[
        [
            { id: 'edit', label: 'Edit', iconLeading: <EditIcon />, shortcut: '⌘E', onSelect: onEdit },
            { id: 'duplicate', label: 'Duplicate', iconLeading: <CopyIcon />, shortcut: '⌘D' },
        ],
        [{ id: 'delete', label: 'Delete', iconLeading: <TrashIcon />, destructive: true, onSelect: onDelete }],
    ]}>
    <button type="button">Options</button>
</Dropdown>

// Account menu with a header + footer section
<Dropdown
    header={{ name: 'Olivia Rhye', email: 'olivia@untitledui.com', avatarInitials: 'OR' }}
    items={[[{ id: 'profile', label: 'View profile' }, { id: 'settings', label: 'Settings' }]]}
    footer={[{ id: 'logout', label: 'Sign out', iconLeading: <LogoutIcon /> }]}>
    <button type="button" aria-label="Account">OR</button>
</Dropdown>

// Checkbox rows (controlled — keep your own checked state; menu stays open)
<Dropdown items={[[
    { id: 'done', label: 'Show completed', checkbox: true, checked: showDone, onSelect: toggleDone },
]]}>
    <button type="button">View</button>
</Dropdown>`

export const DropdownShowcase: React.FC = () => {
    const [showCompleted, setShowCompleted] = useState(true)
    const [showArchived, setShowArchived] = useState(false)

    return (
        <Section
            title="Dropdown (v2 component)"
            description={
                <>
                    Generated from Figma's <span className="font-semibold">Dropdown</span> set
                    (ComponentSet <InlineCode>5:8927</InlineCode>). The design-system action /
                    command menu built on <InlineCode>@radix-ui/react-dropdown-menu</InlineCode> —
                    portalled (never clipped), collision-aware, with roving-focus keyboard nav and
                    typeahead. The trigger is whatever you pass as the single child; the menu is
                    data-driven via <InlineCode>items</InlineCode> sections. Import v2 via{' '}
                    <InlineCode>@trustoo/components/Dropdown/v2</InlineCode> (net-new — no v1 barrel
                    yet).
                </>
            }>
            <div className="space-y-spacing-3xl">
                <div className="space-y-spacing-md">
                    <h3 className="text-sm font-semibold text-text-primary">Action menu</h3>
                    <p className="text-xs text-text-tertiary">
                        Sections are separated by dividers; rows take a leading icon and a trailing
                        keyboard-shortcut hint. The last item is{' '}
                        <InlineCode>destructive</InlineCode>.
                    </p>
                    <div className="flex flex-wrap items-start gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-3xl">
                        <Dropdown
                            items={[
                                [
                                    {
                                        id: 'edit',
                                        label: 'Edit',
                                        iconLeading: <MIcon name="edit" className="text-lg" />,
                                        shortcut: '⌘E',
                                    },
                                    {
                                        id: 'duplicate',
                                        label: 'Duplicate',
                                        iconLeading: (
                                            <MIcon name="content_copy" className="text-lg" />
                                        ),
                                        shortcut: '⌘D',
                                    },
                                    {
                                        id: 'archive',
                                        label: 'Archive',
                                        iconLeading: <MIcon name="archive" className="text-lg" />,
                                    },
                                ],
                                [
                                    {
                                        id: 'share',
                                        label: 'Share',
                                        iconLeading: <MIcon name="share" className="text-lg" />,
                                    },
                                    {
                                        id: 'delete',
                                        label: 'Delete',
                                        iconLeading: <MIcon name="delete" className="text-lg" />,
                                        destructive: true,
                                    },
                                ],
                            ]}>
                            <button
                                type="button"
                                className="inline-flex items-center gap-spacing-md rounded-radius-sm border border-border-primary bg-base-white px-3.5 py-spacing-md text-sm font-medium text-text-secondary shadow-xs hover:bg-background-bg-primary_hover">
                                Options
                                <MIcon name="expand_more" className="text-lg text-text-tertiary" />
                            </button>
                        </Dropdown>
                    </div>
                </div>

                <div className="space-y-spacing-md">
                    <h3 className="text-sm font-semibold text-text-primary">Trigger shapes</h3>
                    <p className="text-xs text-text-tertiary">
                        Any element works as the trigger (Radix <InlineCode>asChild</InlineCode>) —
                        a button, an icon button, or an avatar. The account menu adds a{' '}
                        <InlineCode>header</InlineCode> and a <InlineCode>footer</InlineCode>{' '}
                        section.
                    </p>
                    <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-3xl">
                        <Dropdown
                            items={[
                                [
                                    {
                                        id: 'rename',
                                        label: 'Rename',
                                        iconLeading: <MIcon name="edit" className="text-lg" />,
                                    },
                                    {
                                        id: 'move',
                                        label: 'Move to…',
                                        iconLeading: (
                                            <MIcon name="drive_file_move" className="text-lg" />
                                        ),
                                    },
                                    {
                                        id: 'remove',
                                        label: 'Delete',
                                        iconLeading: <MIcon name="delete" className="text-lg" />,
                                        destructive: true,
                                    },
                                ],
                            ]}>
                            <button
                                type="button"
                                aria-label="More options"
                                className="inline-flex size-9 items-center justify-center rounded-radius-sm border border-border-primary bg-base-white text-text-tertiary shadow-xs hover:bg-background-bg-primary_hover">
                                <MIcon name="more_vert" className="text-xl" />
                            </button>
                        </Dropdown>

                        <Dropdown
                            placement="bottom-end"
                            header={{
                                name: 'Olivia Rhye',
                                email: 'olivia@untitledui.com',
                                avatarInitials: 'OR',
                            }}
                            items={[
                                [
                                    {
                                        id: 'profile',
                                        label: 'View profile',
                                        iconLeading: <MIcon name="person" className="text-lg" />,
                                    },
                                    {
                                        id: 'settings',
                                        label: 'Settings',
                                        iconLeading: <MIcon name="settings" className="text-lg" />,
                                        shortcut: '⌘S',
                                    },
                                ],
                            ]}
                            footer={[
                                {
                                    id: 'logout',
                                    label: 'Sign out',
                                    iconLeading: <MIcon name="logout" className="text-lg" />,
                                },
                            ]}>
                            <button
                                type="button"
                                aria-label="Account menu"
                                className="inline-flex size-10 items-center justify-center rounded-radius-full bg-neutral-200 text-sm font-semibold text-text-tertiary">
                                OR
                            </button>
                        </Dropdown>
                    </div>
                </div>

                <div className="space-y-spacing-md">
                    <h3 className="text-sm font-semibold text-text-primary">Checkbox rows</h3>
                    <p className="text-xs text-text-tertiary">
                        Multi-toggle rows keep their own checked state (the menu stays open on
                        select).
                    </p>
                    <div className="flex flex-wrap items-center gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-3xl">
                        <Dropdown
                            items={[
                                [
                                    {
                                        id: 'completed',
                                        label: 'Show completed',
                                        checkbox: true,
                                        checked: showCompleted,
                                        onSelect: () => setShowCompleted((v) => !v),
                                    },
                                    {
                                        id: 'archived',
                                        label: 'Show archived',
                                        checkbox: true,
                                        checked: showArchived,
                                        onSelect: () => setShowArchived((v) => !v),
                                    },
                                ],
                            ]}>
                            <button
                                type="button"
                                className="inline-flex items-center gap-spacing-md rounded-radius-sm border border-border-primary bg-base-white px-3.5 py-spacing-md text-sm font-medium text-text-secondary shadow-xs hover:bg-background-bg-primary_hover">
                                <MIcon name="filter_list" className="text-lg text-text-tertiary" />
                                View
                            </button>
                        </Dropdown>
                        <span className="text-xs text-text-tertiary">
                            Completed: <InlineCode>{String(showCompleted)}</InlineCode> · Archived:{' '}
                            <InlineCode>{String(showArchived)}</InlineCode>
                        </span>
                    </div>
                </div>

                <div>
                    <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                        How to use
                    </h3>
                    <CodeSnippet code={DROPDOWN_USAGE} />
                    <p className="mt-spacing-lg text-xs text-text-tertiary">
                        Props: <InlineCode>items</InlineCode> (array of sections; each item{' '}
                        <InlineCode>
                            {
                                '{ id, label, iconLeading?, shortcut?, checkbox?, checked?, disabled?, destructive?, onSelect? }'
                            }
                        </InlineCode>
                        ), optional <InlineCode>header</InlineCode> (
                        <InlineCode>{'{ name, email?, avatarSrc?, avatarInitials? }'}</InlineCode>)
                        and <InlineCode>footer</InlineCode> section,{' '}
                        <InlineCode>placement</InlineCode> (bottom-start · bottom-end · top-start ·
                        top-end), and a controlled <InlineCode>open</InlineCode> /{' '}
                        <InlineCode>onOpenChange</InlineCode>. The single child is the trigger
                        (Radix <InlineCode>asChild</InlineCode>).
                    </p>
                </div>
            </div>
        </Section>
    )
}
